import { api, API_URL, authStore, snakeToCamel } from '@/core/api/client'

import type { AttendeeSource, AttendeeStatus } from '../stores'

export interface PBAttendee {
  id: string
  sessionId: string
  studentId: string
  registeredAt: string
  checkedInAt?: string | null
  quizAnswers: Record<string, number>
  quizScore: number | null
  status: AttendeeStatus
  source: AttendeeSource
  firstName: string
  lastName: string
  phone: string
  company: string
  order: string | null
  sessionDetail?: {
    courseName?: string
    locationName?: string
    date?: string
    startTime?: string
    endTime?: string
  } | null
}

const attendeesCollection = api.collection<PBAttendee>('attendees')

/** Fetch all attendees for a given session.
 *  Uses the backend filter ?session= to avoid loading all attendees client-side.
 */
export async function getSessionAttendees(sessionId: string): Promise<PBAttendee[]> {
  const params = new URLSearchParams()
  params.set('session', sessionId)
  params.set('page_size', '1000')

  const res = await fetch(`${API_URL}/attendees/?${params.toString()}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(authStore.token
        ? { Authorization: `Bearer ${authStore.token}` }
        : {}),
    },
  })
  if (!res.ok) {throw new Error(`Failed to list attendees for session ${sessionId}`)}
  const data = await res.json()
  // Backend returns snake_case; convert to camelCase
  return (data.results ?? []).map((item: Record<string, unknown>) => ({
    id: String(item.id),
    sessionId: String(item.session ?? item.session_id ?? item.sessionId),
    studentId: String(item.student_email ?? item.studentId ?? item.student_id ?? ''),
    registeredAt: String(item.registered_at ?? item.registeredAt ?? ''),
    checkedInAt: (item.checked_in_at ?? item.checkedInAt ?? null) as string | null,
    quizAnswers: (item.quiz_answers ?? item.quizAnswers ?? {}) as Record<string, number>,
    quizScore: (item.quiz_score ?? item.quizScore ?? null) as number | null,
    status: String(item.status) as AttendeeStatus,
    source: (item.source as AttendeeSource) || 'manual',
    firstName: String(item.first_name ?? item.firstName ?? ''),
    lastName: String(item.last_name ?? item.lastName ?? ''),
    phone: String(item.phone ?? ''),
    company: String(item.company ?? ''),
    order: item.order ? String(item.order) : null,
    sessionDetail: item.session_detail
      ? {
          courseName: String((item.session_detail as Record<string, unknown>)?.course_name ?? ''),
          locationName: String((item.session_detail as Record<string, unknown>)?.location_name ?? ''),
          date: String((item.session_detail as Record<string, unknown>)?.date ?? ''),
          startTime: String((item.session_detail as Record<string, unknown>)?.start_time ?? ''),
          endTime: String((item.session_detail as Record<string, unknown>)?.end_time ?? ''),
        }
      : null,
  }))
}

/** Fetch all attendee records (admin/instructor view). */
export async function getAllAttendees(): Promise<PBAttendee[]> {
  const res = await fetch(`${API_URL}/attendees/?page_size=10000`, {
    headers: {
      'Content-Type': 'application/json',
      ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}),
    },
  })
  if (!res.ok) {throw new Error('Failed to load attendees')}
  const data = await res.json()
  return (data.results ?? []).map((item: Record<string, unknown>) => ({
    id: String(item.id),
    sessionId: String(item.session ?? item.session_id ?? item.sessionId),
    studentId: String(item.student_email ?? item.studentId ?? item.student_id ?? ''),
    registeredAt: String(item.registered_at ?? item.registeredAt ?? ''),
    checkedInAt: (item.checked_in_at ?? item.checkedInAt ?? null) as string | null,
    quizAnswers: (item.quiz_answers ?? item.quizAnswers ?? {}) as Record<string, number>,
    quizScore: (item.quiz_score ?? item.quizScore ?? null) as number | null,
    status: String(item.status) as AttendeeStatus,
    source: (item.source as AttendeeSource) || 'manual',
    firstName: String(item.first_name ?? item.firstName ?? ''),
    lastName: String(item.last_name ?? item.lastName ?? ''),
    phone: String(item.phone ?? ''),
    company: String(item.company ?? ''),
    order: item.order ? String(item.order) : null,
    sessionDetail: item.session_detail
      ? {
          courseName: String((item.session_detail as Record<string, unknown>)?.course_name ?? ''),
          locationName: String((item.session_detail as Record<string, unknown>)?.location_name ?? ''),
          date: String((item.session_detail as Record<string, unknown>)?.date ?? ''),
          startTime: String((item.session_detail as Record<string, unknown>)?.start_time ?? ''),
          endTime: String((item.session_detail as Record<string, unknown>)?.end_time ?? ''),
        }
      : null,
  }))
}

/** Fetch all attendee records for the currently authenticated user. */
export async function getMyAttendees(): Promise<PBAttendee[]> {
  const res = await fetch(`${API_URL}/attendees/my-enrollments/`, {
    headers: {
      'Content-Type': 'application/json',
      ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}),
    },
  })
  if (!res.ok) {throw new Error('Failed to load my enrollments')}
  const data = await res.json()
  return (data ?? []).map((item: Record<string, unknown>) => ({
    id: String(item.id),
    sessionId: String(item.session ?? item.session_id ?? item.sessionId),
    studentId: String(item.student_email ?? item.studentId ?? item.student_id ?? ''),
    registeredAt: String(item.registered_at ?? item.registeredAt ?? ''),
    checkedInAt: (item.checked_in_at ?? item.checkedInAt ?? null) as string | null,
    quizAnswers: (item.quiz_answers ?? item.quizAnswers ?? {}) as Record<string, number>,
    quizScore: (item.quiz_score ?? item.quizScore ?? null) as number | null,
    status: String(item.status) as AttendeeStatus,
    source: (item.source as AttendeeSource) || 'manual',
    firstName: String(item.first_name ?? item.firstName ?? ''),
    lastName: String(item.last_name ?? item.lastName ?? ''),
    phone: String(item.phone ?? ''),
    company: String(item.company ?? ''),
    order: item.order ? String(item.order) : null,
    sessionDetail: item.session_detail
      ? {
          courseName: String((item.session_detail as Record<string, unknown>)?.course_name ?? ''),
          locationName: String((item.session_detail as Record<string, unknown>)?.location_name ?? ''),
          date: String((item.session_detail as Record<string, unknown>)?.date ?? ''),
          startTime: String((item.session_detail as Record<string, unknown>)?.start_time ?? ''),
          endTime: String((item.session_detail as Record<string, unknown>)?.end_time ?? ''),
        }
      : null,
  }))
}

/** Fetch a single attendee by ID. */
export async function getAttendee(id: string): Promise<PBAttendee> {
  return attendeesCollection.getOne(id)
}

/** Create a new attendee record. */
export async function createAttendee(data: {
  sessionId: string
  studentId: string
  registeredAt?: string
  status?: AttendeeStatus
  source?: AttendeeSource
  firstName?: string
  lastName?: string
  phone?: string
  company?: string
  quizAnswers?: Record<string, number>
  quizScore?: number | null
  /** Session QR capability token — required for anonymous public check-in. */
  qrToken?: string
}): Promise<PBAttendee> {
  return attendeesCollection.create(data)
}

/** Update an existing attendee (e.g. quiz results or contact info). */
export async function updateAttendee(
  id: string,
  data: Partial<{
    sessionId: string
    studentId: string
    registeredAt: string
    status: AttendeeStatus
    quizAnswers: Record<string, number>
    quizScore: number | null
    firstName: string
    lastName: string
    phone: string
    company: string
  }>
): Promise<PBAttendee> {
  return attendeesCollection.update(id, data)
}

/** Delete an attendee record. */
export async function deleteAttendee(id: string): Promise<void> {
  await attendeesCollection.delete(id)
}

export interface AttendeeHistory {
  attendee: PBAttendee
  order: Record<string, unknown> | null
  payments: Array<Record<string, unknown>>
  otherSessions: Array<{
    id: string
    status: string
    courseName: string | null
    sessionDate: string | null
    sessionLocation: string | null
    registeredAt: string | null
    checkedInAt: string | null
    quizScore: number | null
  }>
}

/** Fetch full history for an attendee: order, payments, and other enrollments. */
export async function getAttendeeHistory(id: string): Promise<AttendeeHistory> {
  const res = await fetch(`${API_URL}/attendees/${id}/history/`, {
    headers: {
      'Content-Type': 'application/json',
      ...(authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {}),
    },
  })
  if (!res.ok) {throw new Error(`Failed to load history for attendee ${id}`)}
  return snakeToCamel<AttendeeHistory>(await res.json())
}

